const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4000/api';

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public fields?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends RequestInit {
  token?: string;
  orgId?: string;
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { token, orgId, ...init } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string>),
  };

  if (token) headers.Authorization = `Bearer ${token}`;
  if (orgId) headers['X-Organization-Id'] = orgId;

  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const err = data.error ?? {};
    throw new ApiError(
      err.code ?? 'unknown_error',
      err.message ?? 'An error occurred',
      err.fields,
    );
  }

  return data as T;
}

export const api = {
  register: (body: { name: string; email: string; password: string }) =>
    request<{ data: { user: unknown; message: string; verifyUrl?: string } }>(
      '/auth/register',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  login: (body: { email: string; password: string }) =>
    request<{
      data: {
        user: User;
        organizations: Organization[];
        accessToken: string;
        refreshToken: string;
      };
    }>('/auth/login', { method: 'POST', body: JSON.stringify(body) }),

  logout: (token: string, refreshToken: string) =>
    request('/auth/logout', {
      method: 'POST',
      token,
      body: JSON.stringify({ refreshToken }),
    }),

  me: (token: string) =>
    request<{ data: { user: User; organizations: Organization[] } }>(
      '/auth/me',
      { token },
    ),

  verifyEmail: (token: string) =>
    request('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  forgotPassword: (email: string) =>
    request<{ data: { message: string; resetUrl?: string } }>(
      '/auth/forgot-password',
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      },
    ),

  resetPassword: (token: string, password: string) =>
    request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  createOrganization: (
    token: string,
    body: Record<string, unknown>,
  ) =>
    request<{ data: Organization }>('/organizations', {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),

  getOrganization: (token: string, orgId: string) =>
    request<{ data: Organization & { kyc?: unknown } }>(
      `/organizations/${orgId}`,
      { token },
    ),

  updateOrganization: (
    token: string,
    orgId: string,
    body: Partial<{
      displayName: string;
      legalName: string;
      industry: string;
      registrationNumber: string;
      taxNumber: string;
      contactPerson: string;
      contactEmail: string;
      contactPhone: string;
      timezone: string;
    }>,
  ) =>
    request<{ data: Organization }>(`/organizations/${orgId}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(body),
    }),

  getOnboarding: (token: string, orgId: string) =>
    request<{ data: OnboardingStatus }>(
      `/organizations/${orgId}/onboarding`,
      { token },
    ),

  submitKyc: (
    token: string,
    orgId: string,
    body: Record<string, string>,
  ) =>
    request(`/organizations/${orgId}/kyc`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),

  getDashboard: (token: string, orgId: string) =>
    request<{ data: DashboardStats }>(
      `/organizations/${orgId}/dashboard`,
      { token, orgId },
    ),

  searchDashboard: (token: string, orgId: string, q: string) =>
    request<{ data: DashboardSearchResults }>(
      `/organizations/${orgId}/dashboard/search?q=${encodeURIComponent(q)}`,
      { token, orgId },
    ),

  adminDashboard: (token: string) =>
    request<{ data: AdminDashboard }>('/admin/dashboard', { token }),

  adminUsers: (
    token: string,
    params?: { cursor?: string; limit?: number; search?: string; status?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.search) qs.set('search', params.search);
    if (params?.status) qs.set('status', params.status);
    const query = qs.toString();
    return request<{ data: AdminUser[]; pagination: CursorPagination }>(
      `/admin/users${query ? `?${query}` : ''}`,
      { token },
    );
  },

  adminAuditLogs: (
    token: string,
    params?: {
      organizationId?: string;
      action?: string;
      resourceType?: string;
      start?: string;
      end?: string;
      cursor?: string;
      limit?: number;
    },
  ) => {
    const qs = new URLSearchParams();
    if (params?.organizationId) qs.set('organizationId', params.organizationId);
    if (params?.action) qs.set('action', params.action);
    if (params?.resourceType) qs.set('resourceType', params.resourceType);
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{ data: AuditLogEntry[]; pagination: CursorPagination }>(
      `/admin/audit-logs${query ? `?${query}` : ''}`,
      { token },
    );
  },

  adminExportAuditLogs: (
    token: string,
    params?: {
      organizationId?: string;
      action?: string;
      resourceType?: string;
      start?: string;
      end?: string;
    },
  ) => {
    const qs = new URLSearchParams();
    if (params?.organizationId) qs.set('organizationId', params.organizationId);
    if (params?.action) qs.set('action', params.action);
    if (params?.resourceType) qs.set('resourceType', params.resourceType);
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    return request<{ data: { csv: string; count: number } }>(
      `/admin/audit-logs/export?${qs}`,
      { token },
    );
  },

  adminOrganizations: (
    token: string,
    params?: { cursor?: string; limit?: number; search?: string; status?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.search) qs.set('search', params.search);
    if (params?.status) qs.set('status', params.status);
    const query = qs.toString();
    return request<{ data: AdminOrg[]; pagination: CursorPagination }>(
      `/admin/organizations${query ? `?${query}` : ''}`,
      { token },
    );
  },

  adminExportOrganizations: (
    token: string,
    params?: { start?: string; end?: string; search?: string; status?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.search) qs.set('search', params.search);
    if (params?.status) qs.set('status', params.status);
    return request<{ data: { csv: string; count: number } }>(
      `/admin/organizations/export?${qs}`,
      { token },
    );
  },

  adminSuspendOrganization: (token: string, orgId: string, reason?: string) =>
    request(`/admin/organizations/${orgId}/suspend`, {
      method: 'POST',
      token,
      body: JSON.stringify({ reason }),
    }),

  adminPendingKyc: (
    token: string,
    params?: { cursor?: string; limit?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{ data: AdminKycProfile[]; pagination: CursorPagination }>(
      `/admin/kyc/pending${query ? `?${query}` : ''}`,
      { token },
    );
  },

  adminExportKyc: (
    token: string,
    params?: { start?: string; end?: string; status?: string; orgId?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.status) qs.set('status', params.status);
    if (params?.orgId) qs.set('orgId', params.orgId);
    return request<{ data: { csv: string; count: number } }>(
      `/admin/kyc/export?${qs}`,
      { token },
    );
  },

  adminApproveKyc: (token: string, kycId: string) =>
    request(`/admin/kyc/${kycId}/approve`, { method: 'POST', token }),

  adminRejectKyc: (token: string, kycId: string, notes: string) =>
    request(`/admin/kyc/${kycId}/reject`, {
      method: 'POST',
      token,
      body: JSON.stringify({ notes }),
    }),

  adminRunPostpaidCycle: (token: string, asOf?: string) =>
    request<{ data: PostpaidCycleResult }>('/admin/billing/run-postpaid-cycle', {
      method: 'POST',
      token,
      body: JSON.stringify(asOf ? { asOf } : {}),
    }),

  adminListOutstandingInvoices: (
    token: string,
    params?: { orgId?: string; cursor?: string; limit?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.orgId) qs.set('orgId', params.orgId);
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{ data: Invoice[]; pagination: CursorPagination }>(
      `/admin/invoices/outstanding${query ? `?${query}` : ''}`,
      { token },
    );
  },

  adminExportOutstandingInvoices: (token: string, params?: { orgId?: string }) => {
    const qs = new URLSearchParams();
    if (params?.orgId) qs.set('orgId', params.orgId);
    return request<{ data: { csv: string; count: number } }>(
      `/admin/invoices/outstanding/export?${qs}`,
      { token },
    );
  },

  adminRecordInvoicePayment: (
    token: string,
    invoiceId: string,
    amountMinor: number,
    note?: string,
  ) =>
    request<{ data: Invoice }>(`/admin/invoices/${invoiceId}/record-payment`, {
      method: 'POST',
      token,
      body: JSON.stringify({ amountMinor, note }),
    }),

  adminPendingPayments: (
    token: string,
    params?: { cursor?: string; limit?: number; orgId?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.orgId) qs.set('orgId', params.orgId);
    const query = qs.toString();
    return request<{ data: AdminPendingPayment[]; pagination: CursorPagination }>(
      `/admin/payments/pending${query ? `?${query}` : ''}`,
      { token },
    );
  },

  adminExportPayments: (
    token: string,
    params?: { status?: string; orgId?: string; start?: string; end?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.orgId) qs.set('orgId', params.orgId);
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    return request<{ data: { csv: string; count: number } }>(
      `/admin/payments/export?${qs}`,
      { token },
    );
  },

  adminConfirmPayment: (token: string, paymentId: string) =>
    request(`/admin/payments/${paymentId}/confirm`, { method: 'POST', token }),

  // Contacts
  listContacts: (token: string, orgId: string, params?: { search?: string; cursor?: string; limit?: number; groupId?: string; tag?: string }) => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set('search', params.search);
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.groupId) qs.set('groupId', params.groupId);
    if (params?.tag) qs.set('tag', params.tag);
    return request<{ data: Contact[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/contacts?${qs}`,
      { token, orgId },
    );
  },

  createContact: (token: string, orgId: string, body: Partial<Contact>) =>
    request<{ data: Contact }>(`/organizations/${orgId}/contacts`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  importContacts: (token: string, orgId: string, csv: string) =>
    request<{ data: ImportResult }>(`/organizations/${orgId}/contacts/import`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify({ csv }),
    }),

  listContactImports: (
    token: string,
    orgId: string,
    params?: { cursor?: string; limit?: number; start?: string; end?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    const query = qs.toString();
    return request<{ data: ContactImport[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/contacts/imports${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  exportContactImports: (
    token: string,
    orgId: string,
    params?: { start?: string; end?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/contacts/imports/export?${qs}`,
      { token, orgId },
    );
  },

  exportContacts: (
    token: string,
    orgId: string,
    params?: { search?: string; groupId?: string; tag?: string; start?: string; end?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set('search', params.search);
    if (params?.groupId) qs.set('groupId', params.groupId);
    if (params?.tag) qs.set('tag', params.tag);
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/contacts/export?${qs}`,
      { token, orgId },
    );
  },

  deleteContact: (token: string, orgId: string, contactId: string) =>
    request(`/organizations/${orgId}/contacts/${contactId}`, {
      method: 'DELETE',
      token,
      orgId,
    }),

  getContact: (token: string, orgId: string, contactId: string) =>
    request<{ data: Contact }>(`/organizations/${orgId}/contacts/${contactId}`, {
      token,
      orgId,
    }),

  updateContact: (
    token: string,
    orgId: string,
    contactId: string,
    body: Partial<Pick<Contact, 'firstName' | 'lastName' | 'email' | 'consentStatus' | 'tags'>>,
  ) =>
    request<{ data: Contact }>(`/organizations/${orgId}/contacts/${contactId}`, {
      method: 'PATCH',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  listContactTags: (
    token: string,
    orgId: string,
    params?: { cursor?: string; limit?: number; start?: string; end?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    const query = qs.toString();
    return request<{ data: ContactTag[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/contacts/tags${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  bulkAssignContactTags: (
    token: string,
    orgId: string,
    body: { contactIds: string[]; tags: string[]; mode?: 'add' | 'replace' },
  ) =>
    request<{ data: { updated: number; tags: string[]; mode: string } }>(
      `/organizations/${orgId}/contacts/tags/bulk`,
      {
        method: 'POST',
        token,
        orgId,
        body: JSON.stringify(body),
      },
    ),

  listContactGroups: (
    token: string,
    orgId: string,
    params?: { cursor?: string; limit?: number; start?: string; end?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    const query = qs.toString();
    return request<{ data: ContactGroup[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/contact-groups${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  exportContactGroups: (
    token: string,
    orgId: string,
    params?: { start?: string; end?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/contact-groups/export?${qs}`,
      { token, orgId },
    );
  },

  exportContactTags: (
    token: string,
    orgId: string,
    params?: { start?: string; end?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/contacts/tags/export?${qs}`,
      { token, orgId },
    );
  },

  createContactGroup: (
    token: string,
    orgId: string,
    body: { name: string; description?: string },
  ) =>
    request<{ data: ContactGroup }>(`/organizations/${orgId}/contact-groups`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  addContactsToGroup: (
    token: string,
    orgId: string,
    groupId: string,
    contactIds: string[],
  ) =>
    request<{ data: { added: number } }>(
      `/organizations/${orgId}/contact-groups/${groupId}/members`,
      {
        method: 'POST',
        token,
        orgId,
        body: JSON.stringify({ contactIds }),
      },
    ),

  removeContactsFromGroup: (
    token: string,
    orgId: string,
    groupId: string,
    contactIds: string[],
  ) =>
    request<{ data: { removed: number } }>(
      `/organizations/${orgId}/contact-groups/${groupId}/members`,
      {
        method: 'DELETE',
        token,
        orgId,
        body: JSON.stringify({ contactIds }),
      },
    ),

  deleteContactGroup: (token: string, orgId: string, groupId: string) =>
    request<{ data: { deleted: boolean } }>(
      `/organizations/${orgId}/contact-groups/${groupId}`,
      { method: 'DELETE', token, orgId },
    ),

  // Sender IDs
  listSenderIds: (
    token: string,
    orgId: string,
    params?: { cursor?: string; limit?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{ data: SenderId[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/sender-ids${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  listApprovedSenderIds: (token: string, orgId: string) =>
    request<{ data: Array<{ id: string; value: string; countryCode: string }> }>(
      `/organizations/${orgId}/sender-ids/approved`,
      { token, orgId },
    ),

  requestSenderId: (
    token: string,
    orgId: string,
    body: { value: string; countryCode: string; purpose?: string; sampleMessage?: string },
  ) =>
    request<{ data: SenderId }>(`/organizations/${orgId}/sender-ids/requests`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  // SMS
  estimateSms: (token: string, orgId: string, body: { to: string; message: string }) =>
    request<{ data: SmsEstimate }>(`/organizations/${orgId}/sms/estimate`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  sendSms: (token: string, orgId: string, body: { to: string; from: string; message: string }) =>
    request<{ data: SmsMessage }>(`/organizations/${orgId}/sms/messages`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  listMessages: (
    token: string,
    orgId: string,
    params?: {
      status?: string;
      cursor?: string;
      start?: string;
      end?: string;
      campaignId?: string;
      limit?: number;
    },
  ) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.campaignId) qs.set('campaignId', params.campaignId);
    return request<{ data: SmsMessage[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/sms/messages?${qs}`,
      { token, orgId },
    );
  },

  exportSmsMessages: (
    token: string,
    orgId: string,
    params?: { status?: string; start?: string; end?: string; campaignId?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.campaignId) qs.set('campaignId', params.campaignId);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/sms/messages/export?${qs}`,
      { token, orgId },
    );
  },

  getMessage: (token: string, orgId: string, messageId: string) =>
    request<{ data: SmsMessageDetail }>(`/organizations/${orgId}/sms/messages/${messageId}`, {
      token,
      orgId,
    }),

  listAuditLogs: (
    token: string,
    orgId: string,
    params?: {
      action?: string;
      resourceType?: string;
      start?: string;
      end?: string;
      limit?: number;
      cursor?: string;
    },
  ) => {
    const qs = new URLSearchParams();
    if (params?.action) qs.set('action', params.action);
    if (params?.resourceType) qs.set('resourceType', params.resourceType);
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.cursor) qs.set('cursor', params.cursor);
    return request<{ data: AuditLogEntry[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/audit-logs?${qs}`,
      { token, orgId },
    );
  },

  exportAuditLogs: (
    token: string,
    orgId: string,
    params?: {
      action?: string;
      resourceType?: string;
      start?: string;
      end?: string;
    },
  ) => {
    const qs = new URLSearchParams();
    if (params?.action) qs.set('action', params.action);
    if (params?.resourceType) qs.set('resourceType', params.resourceType);
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/audit-logs/export?${qs}`,
      { token, orgId },
    );
  },

  listSmsTemplates: (
    token: string,
    orgId: string,
    params?: { cursor?: string; limit?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{ data: SmsTemplate[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/sms/templates${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  createSmsTemplate: (
    token: string,
    orgId: string,
    body: { name: string; content: string },
  ) =>
    request<{ data: SmsTemplate }>(`/organizations/${orgId}/sms/templates`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  deleteSmsTemplate: (token: string, orgId: string, templateId: string) =>
    request<{ data: { deleted: boolean } }>(
      `/organizations/${orgId}/sms/templates/${templateId}`,
      { method: 'DELETE', token, orgId },
    ),

  bulkDeleteSmsTemplates: (token: string, orgId: string, templateIds: string[]) =>
    request<{ data: { deleted: number } }>(
      `/organizations/${orgId}/sms/templates/bulk-delete`,
      {
        method: 'POST',
        token,
        orgId,
        body: JSON.stringify({ templateIds }),
      },
    ),

  updateSmsTemplate: (
    token: string,
    orgId: string,
    templateId: string,
    body: { name?: string; content?: string },
  ) =>
    request<{ data: SmsTemplate }>(`/organizations/${orgId}/sms/templates/${templateId}`, {
      method: 'PATCH',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  listSuppressions: (
    token: string,
    orgId: string,
    params?: {
      cursor?: string;
      limit?: number;
      start?: string;
      end?: string;
      destination?: string;
    },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.destination) qs.set('destination', params.destination);
    const query = qs.toString();
    return request<{ data: SuppressionEntry[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/sms/suppressions${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  exportSmsSuppressions: (
    token: string,
    orgId: string,
    params?: { start?: string; end?: string; destination?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.destination) qs.set('destination', params.destination);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/sms/suppressions/export?${qs}`,
      { token, orgId },
    );
  },

  addSuppression: (token: string, orgId: string, body: { destination: string; reason?: string }) =>
    request<{ data: SuppressionEntry }>(`/organizations/${orgId}/sms/suppressions`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  removeSuppression: (token: string, orgId: string, entryId: string) =>
    request(`/organizations/${orgId}/sms/suppressions/${entryId}`, {
      method: 'DELETE',
      token,
      orgId,
    }),

  getWallet: (token: string, orgId: string) =>
    request<{ data: WalletBalance }>(`/organizations/${orgId}/wallet`, {
      token,
      orgId,
    }),

  adminPendingSenderIds: (
    token: string,
    params?: { cursor?: string; limit?: number; orgId?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.orgId) qs.set('orgId', params.orgId);
    const query = qs.toString();
    return request<{ data: AdminPendingSenderId[]; pagination: CursorPagination }>(
      `/admin/sender-ids/pending${query ? `?${query}` : ''}`,
      { token },
    );
  },

  adminExportSenderIds: (token: string, params?: { orgId?: string }) => {
    const qs = new URLSearchParams();
    if (params?.orgId) qs.set('orgId', params.orgId);
    return request<{ data: { csv: string; count: number } }>(
      `/admin/sender-ids/export?${qs}`,
      { token },
    );
  },

  adminApproveSenderId: (token: string, id: string) =>
    request(`/admin/sender-ids/${id}/approve`, { method: 'POST', token }),

  adminRejectSenderId: (token: string, id: string, reason?: string) =>
    request(`/admin/sender-ids/${id}/reject`, {
      method: 'POST',
      token,
      body: JSON.stringify({ reason: reason || 'Rejected' }),
    }),

  // Campaigns
  listCampaigns: (
    token: string,
    orgId: string,
    params?: { status?: string; cursor?: string; limit?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{ data: Campaign[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/campaigns${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  exportCampaigns: (
    token: string,
    orgId: string,
    params?: { status?: string; start?: string; end?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/campaigns/export?${qs}`,
      { token, orgId },
    );
  },

  exportCampaignMessages: (
    token: string,
    orgId: string,
    campaignId: string,
    params?: { status?: string; start?: string; end?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/campaigns/${campaignId}/messages/export?${qs}`,
      { token, orgId },
    );
  },

  exportCampaignAudience: (
    token: string,
    orgId: string,
    campaignId: string,
    params?: { status?: string; start?: string; end?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/campaigns/${campaignId}/audience/export?${qs}`,
      { token, orgId },
    );
  },

  createCampaign: (
    token: string,
    orgId: string,
    body: { name: string; senderId: string; content: string },
  ) =>
    request<{ data: Campaign }>(`/organizations/${orgId}/campaigns`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  buildCampaignAudience: (
    token: string,
    orgId: string,
    campaignId: string,
    body: {
      audienceType: 'group' | 'manual' | 'all_contacts' | 'tag';
      groupId?: string;
      tags?: string[];
      recipients?: string[];
    },
  ) =>
    request<{ data: Campaign & { preview?: CampaignPreview } }>(
      `/organizations/${orgId}/campaigns/${campaignId}/audience`,
      { method: 'POST', token, orgId, body: JSON.stringify(body) },
    ),

  launchCampaign: (token: string, orgId: string, campaignId: string, scheduledAt?: string) =>
    request<{ data: Campaign }>(
      `/organizations/${orgId}/campaigns/${campaignId}/launch`,
      {
        method: 'POST',
        token,
        orgId,
        body: JSON.stringify(scheduledAt ? { scheduledAt } : {}),
      },
    ),

  getCampaign: (token: string, orgId: string, campaignId: string) =>
    request<{ data: CampaignDetail }>(
      `/organizations/${orgId}/campaigns/${campaignId}`,
      { token, orgId },
    ),

  updateCampaign: (
    token: string,
    orgId: string,
    campaignId: string,
    body: { name?: string; senderId?: string; content?: string },
  ) =>
    request<{ data: Campaign }>(
      `/organizations/${orgId}/campaigns/${campaignId}`,
      {
        method: 'PATCH',
        token,
        orgId,
        body: JSON.stringify(body),
      },
    ),

  duplicateCampaign: (token: string, orgId: string, campaignId: string) =>
    request<{ data: Campaign }>(
      `/organizations/${orgId}/campaigns/${campaignId}/duplicate`,
      { method: 'POST', token, orgId },
    ),

  cancelCampaign: (token: string, orgId: string, campaignId: string) =>
    request<{ data: Campaign }>(
      `/organizations/${orgId}/campaigns/${campaignId}/cancel`,
      { method: 'POST', token, orgId },
    ),

  pauseCampaign: (token: string, orgId: string, campaignId: string) =>
    request<{ data: Campaign }>(
      `/organizations/${orgId}/campaigns/${campaignId}/pause`,
      { method: 'POST', token, orgId },
    ),

  resumeCampaign: (token: string, orgId: string, campaignId: string) =>
    request<{ data: Campaign }>(
      `/organizations/${orgId}/campaigns/${campaignId}/resume`,
      { method: 'POST', token, orgId },
    ),

  deleteCampaign: (token: string, orgId: string, campaignId: string) =>
    request<{ data: { deleted: boolean } }>(
      `/organizations/${orgId}/campaigns/${campaignId}`,
      { method: 'DELETE', token, orgId },
    ),

  aiSuggestSms: (
    token: string,
    orgId: string,
    body: { topic: string; tone?: string; maxLength?: number },
  ) =>
    request<{ data: { suggestions: string[]; source: 'mpakani' | 'template' } }>(
      `/organizations/${orgId}/ai/suggest-sms`,
      { method: 'POST', token, orgId, body: JSON.stringify(body) },
    ),

  aiSummarizeTicket: (token: string, orgId: string, ticketId: string) =>
    request<{ data: { ticketId: string; summary: string; source: 'mpakani' | 'template' } }>(
      `/organizations/${orgId}/ai/summarize-ticket`,
      { method: 'POST', token, orgId, body: JSON.stringify({ ticketId }) },
    ),

  // Payments / Billing
  initiateTopUp: (token: string, orgId: string, amount: number, phoneNumber?: string) =>
    request<{ data: Payment & { message?: string } }>(`/organizations/${orgId}/payments/topup`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify({ amount, ...(phoneNumber ? { phoneNumber } : {}) }),
    }),

  listPayments: (
    token: string,
    orgId: string,
    params?: { cursor?: string; limit?: number; status?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.status) qs.set('status', params.status);
    const query = qs.toString();
    return request<{ data: Payment[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/payments${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  exportPayments: (
    token: string,
    orgId: string,
    params?: { start?: string; end?: string; status?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.status) qs.set('status', params.status);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/payments/export?${qs}`,
      { token, orgId },
    );
  },

  // Developers
  listProjects: (
    token: string,
    orgId: string,
    params?: { cursor?: string; limit?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{ data: DevProject[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/developers/projects${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  listApiKeys: (
    token: string,
    orgId: string,
    params?: { cursor?: string; limit?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{ data: ApiKey[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/developers/api-keys${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  createApiKey: (
    token: string,
    orgId: string,
    body: { projectId: string; name: string; environment: 'test' | 'live'; scopes?: string[] },
  ) =>
    request<{ data: ApiKey & { secret: string; message: string } }>(
      `/organizations/${orgId}/developers/api-keys`,
      { method: 'POST', token, orgId, body: JSON.stringify(body) },
    ),

  revokeApiKey: (token: string, orgId: string, keyId: string) =>
    request(`/organizations/${orgId}/developers/api-keys/${keyId}`, {
      method: 'DELETE',
      token,
      orgId,
    }),

  listWebhooks: (
    token: string,
    orgId: string,
    params?: { cursor?: string; limit?: number; status?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.status) qs.set('status', params.status);
    const query = qs.toString();
    return request<{ data: WebhookEndpoint[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/developers/webhooks${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  exportWebhooks: (
    token: string,
    orgId: string,
    params?: { start?: string; end?: string; status?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.status) qs.set('status', params.status);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/developers/webhooks/export?${qs}`,
      { token, orgId },
    );
  },

  createWebhook: (
    token: string,
    orgId: string,
    body: { projectId: string; url: string; events: string[] },
  ) =>
    request<{ data: WebhookEndpoint & { signingSecret: string; message: string } }>(
      `/organizations/${orgId}/developers/webhooks`,
      { method: 'POST', token, orgId, body: JSON.stringify(body) },
    ),

  listApiLogs: (
    token: string,
    orgId: string,
    params?: { statusCode?: number; path?: string; cursor?: string; limit?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.statusCode) qs.set('statusCode', String(params.statusCode));
    if (params?.path) qs.set('path', params.path);
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    return request<{ data: ApiLog[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/developers/logs?${qs}`,
      { token, orgId },
    );
  },

  exportApiLogs: (
    token: string,
    orgId: string,
    params?: {
      statusCode?: number;
      path?: string;
      start?: string;
      end?: string;
    },
  ) => {
    const qs = new URLSearchParams();
    if (params?.statusCode) qs.set('statusCode', String(params.statusCode));
    if (params?.path) qs.set('path', params.path);
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/developers/logs/export?${qs}`,
      { token, orgId },
    );
  },

  exportWebhookDeliveries: (
    token: string,
    orgId: string,
    params?: {
      status?: string;
      eventType?: string;
      webhookId?: string;
      start?: string;
      end?: string;
    },
  ) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.eventType) qs.set('eventType', params.eventType);
    if (params?.webhookId) qs.set('webhookId', params.webhookId);
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/developers/webhook-deliveries/export?${qs}`,
      { token, orgId },
    );
  },

  listWebhookDeliveries: (
    token: string,
    orgId: string,
    params?: {
      status?: string;
      eventType?: string;
      webhookId?: string;
      cursor?: string;
      limit?: number;
    },
  ) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.eventType) qs.set('eventType', params.eventType);
    if (params?.webhookId) qs.set('webhookId', params.webhookId);
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    return request<{ data: WebhookDelivery[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/developers/webhook-deliveries?${qs}`,
      { token, orgId },
    );
  },

  retryWebhookDelivery: (token: string, orgId: string, deliveryId: string) =>
    request<{ data: { queued: boolean } }>(
      `/organizations/${orgId}/developers/webhook-deliveries/${deliveryId}/retry`,
      { method: 'POST', token, orgId },
    ),

  reactivateWebhook: (token: string, orgId: string, webhookId: string) =>
    request<{ data: { reactivated: boolean } }>(
      `/organizations/${orgId}/developers/webhooks/${webhookId}/reactivate`,
      { method: 'POST', token, orgId },
    ),

  listMembers: (token: string, orgId: string) =>
    request<{ data: OrganizationMember[] }>(`/organizations/${orgId}/members`, {
      token,
      orgId,
    }),

  inviteMember: (
    token: string,
    orgId: string,
    body: { email: string; roleKey: string },
  ) =>
    request<{ data: OrganizationMember }>(`/organizations/${orgId}/members`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  removeMember: (token: string, orgId: string, memberId: string) =>
    request<{ data: { removed: boolean } }>(
      `/organizations/${orgId}/members/${memberId}`,
      { method: 'DELETE', token, orgId },
    ),

  updateMemberRole: (token: string, orgId: string, memberId: string, roleKey: string) =>
    request<{ data: OrganizationMember }>(
      `/organizations/${orgId}/members/${memberId}`,
      { method: 'PATCH', token, orgId, body: JSON.stringify({ roleKey }) },
    ),

  // Analytics
  getMessageAnalytics: (token: string, orgId: string, start?: string, end?: string) => {
    const qs = new URLSearchParams();
    if (start) qs.set('start', start);
    if (end) qs.set('end', end);
    return request<{ data: MessageAnalytics }>(
      `/organizations/${orgId}/analytics/messages?${qs}`,
      { token, orgId },
    );
  },

  exportAnalytics: (token: string, orgId: string, start?: string, end?: string) => {
    const qs = new URLSearchParams();
    if (start) qs.set('start', start);
    if (end) qs.set('end', end);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/analytics/export?${qs}`,
      { token, orgId },
    );
  },

  exportCampaignAnalytics: (
    token: string,
    orgId: string,
    params?: { start?: string; end?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/analytics/campaigns/export?${qs}`,
      { token, orgId },
    );
  },

  getCampaignAnalytics: (token: string, orgId: string, start?: string, end?: string) => {
    const qs = new URLSearchParams();
    if (start) qs.set('start', start);
    if (end) qs.set('end', end);
    return request<{ data: CampaignAnalytics[] }>(
      `/organizations/${orgId}/analytics/campaigns?${qs}`,
      { token, orgId },
    );
  },

  getAnalyticsInsights: (token: string, orgId: string, start?: string, end?: string) => {
    const qs = new URLSearchParams();
    if (start) qs.set('start', start);
    if (end) qs.set('end', end);
    return request<{ data: AnalyticsInsights }>(
      `/organizations/${orgId}/analytics/insights?${qs}`,
      { token, orgId },
    );
  },

  activateUssdService: (token: string, orgId: string, serviceId: string) =>
    request(`/organizations/${orgId}/channels/ussd/services/${serviceId}/activate`, {
      method: 'POST',
      token,
      orgId,
    }),

  listUssdSessions: (
    token: string,
    orgId: string,
    params?: { serviceId?: string; cursor?: string; limit?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.serviceId) qs.set('serviceId', params.serviceId);
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{ data: UssdSession[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/channels/ussd/sessions${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  createWhatsappAccount: (
    token: string,
    orgId: string,
    body: { phoneNumberId: string; displayName?: string; accessToken: string },
  ) =>
    request<{ data: WhatsappAccount }>(
      `/organizations/${orgId}/channels/whatsapp/accounts`,
      { method: 'POST', token, orgId, body: JSON.stringify(body) },
    ),

  listWhatsappTemplates: (
    token: string,
    orgId: string,
    params?: { accountId?: string; cursor?: string; limit?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.accountId) qs.set('accountId', params.accountId);
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{ data: WhatsappTemplate[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/channels/whatsapp/templates${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  createWhatsappTemplate: (
    token: string,
    orgId: string,
    body: {
      whatsappAccountId: string;
      name: string;
      language?: string;
      category?: string;
      body?: string;
    },
  ) =>
    request<{ data: WhatsappTemplate }>(
      `/organizations/${orgId}/channels/whatsapp/templates`,
      { method: 'POST', token, orgId, body: JSON.stringify(body) },
    ),

  createVoiceCall: (
    token: string,
    orgId: string,
    body: { to: string; voiceNumberId?: string; from?: string },
  ) =>
    request(`/organizations/${orgId}/channels/voice/calls`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  createVoiceNumber: (
    token: string,
    orgId: string,
    body: { number: string; countryCode: string; type?: string },
  ) =>
    request<{ data: VoiceNumber }>(`/organizations/${orgId}/channels/voice/numbers`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  listVoiceCalls: (
    token: string,
    orgId: string,
    params?: { start?: string; end?: string; cursor?: string; limit?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{ data: VoiceCall[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/channels/voice/calls${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  sendWhatsappMessage: (
    token: string,
    orgId: string,
    body: { whatsappAccountId: string; to: string; templateName: string; language?: string },
  ) =>
    request(`/organizations/${orgId}/channels/whatsapp/messages`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  // IVR Studio
  listIvrFlows: (token: string, orgId: string, params?: { status?: string; search?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.search) qs.set('search', params.search);
    const query = qs.toString();
    return request<{ data: IvrFlow[] }>(
      `/organizations/${orgId}/ivr/flows${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  getIvrMetrics: (token: string, orgId: string) =>
    request<{ data: IvrMetrics }>(`/organizations/${orgId}/ivr/metrics`, { token, orgId }),

  createIvrFlow: (
    token: string,
    orgId: string,
    body: { name: string; description?: string; defaultLanguage?: string },
  ) =>
    request<{ data: IvrFlow }>(`/organizations/${orgId}/ivr/flows`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  getIvrFlow: (token: string, orgId: string, flowId: string) =>
    request<{ data: IvrFlow }>(`/organizations/${orgId}/ivr/flows/${flowId}`, { token, orgId }),

  updateIvrFlow: (
    token: string,
    orgId: string,
    flowId: string,
    body: Partial<{
      name: string;
      description: string;
      defaultLanguage: string;
      assignedVoiceNumber: string | null;
      status: string;
    }>,
  ) =>
    request<{ data: IvrFlow }>(`/organizations/${orgId}/ivr/flows/${flowId}`, {
      method: 'PATCH',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  deleteIvrFlow: (token: string, orgId: string, flowId: string) =>
    request(`/organizations/${orgId}/ivr/flows/${flowId}`, {
      method: 'DELETE',
      token,
      orgId,
    }),

  saveIvrDraft: (
    token: string,
    orgId: string,
    flowId: string,
    graph: { nodes: IvrNode[]; edges: IvrEdge[] },
  ) =>
    request<{ data: { nodes: IvrNode[]; edges: IvrEdge[] } }>(
      `/organizations/${orgId}/ivr/flows/${flowId}/draft`,
      { method: 'PUT', token, orgId, body: JSON.stringify({ graph }) },
    ),

  validateIvrFlow: (token: string, orgId: string, flowId: string) =>
    request<IvrValidationResult>(`/organizations/${orgId}/ivr/flows/${flowId}/validate`, {
      method: 'POST',
      token,
      orgId,
    }),

  publishIvrFlow: (token: string, orgId: string, flowId: string) =>
    request<{ published: boolean; version?: number; flow?: IvrFlow; errors?: IvrIssue[] }>(
      `/organizations/${orgId}/ivr/flows/${flowId}/publish`,
      { method: 'POST', token, orgId },
    ),

  pauseIvrFlow: (token: string, orgId: string, flowId: string) =>
    request<{ data: IvrFlow }>(`/organizations/${orgId}/ivr/flows/${flowId}/pause`, {
      method: 'POST',
      token,
      orgId,
    }),

  archiveIvrFlow: (token: string, orgId: string, flowId: string) =>
    request<{ data: IvrFlow }>(`/organizations/${orgId}/ivr/flows/${flowId}/archive`, {
      method: 'POST',
      token,
      orgId,
    }),

  listIvrExecutions: (token: string, orgId: string, params?: { flowId?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.flowId) qs.set('flowId', params.flowId);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{ data: IvrExecution[] }>(
      `/organizations/${orgId}/ivr/executions${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  getIvrExecution: (token: string, orgId: string, executionId: string) =>
    request<{ data: IvrExecutionDetail }>(`/organizations/${orgId}/ivr/executions/${executionId}`, {
      token,
      orgId,
    }),

  startIvrSimulation: (
    token: string,
    orgId: string,
    flowId: string,
    body?: { language?: string },
  ) =>
    request<{ data: IvrSimulation }>(`/organizations/${orgId}/ivr/flows/${flowId}/simulations`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body ?? {}),
    }),

  sendIvrSimulationInput: (
    token: string,
    orgId: string,
    simulationId: string,
    body: { dtmf: string },
  ) =>
    request<{ data: IvrSimulation }>(`/organizations/${orgId}/ivr/simulations/${simulationId}/input`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  // Invoices
  listInvoices: (
    token: string,
    orgId: string,
    params?: { cursor?: string; limit?: number; status?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.status) qs.set('status', params.status);
    const query = qs.toString();
    return request<{ data: Invoice[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/invoices${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  exportInvoices: (
    token: string,
    orgId: string,
    params?: { start?: string; end?: string; status?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.status) qs.set('status', params.status);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/invoices/export?${qs}`,
      { token, orgId },
    );
  },

  getInvoice: (token: string, orgId: string, invoiceId: string) =>
    request<{ data: InvoiceDetail }>(`/organizations/${orgId}/invoices/${invoiceId}`, {
      token,
      orgId,
    }),

  generateInvoice: (
    token: string,
    orgId: string,
    body: { periodStart: string; periodEnd: string },
  ) =>
    request<{ data: InvoiceDetail }>(`/organizations/${orgId}/invoices/generate`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  listWalletTransactions: (
    token: string,
    orgId: string,
    params?: { cursor?: string; start?: string; end?: string; type?: string; limit?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.type) qs.set('type', params.type);
    if (params?.limit) qs.set('limit', String(params.limit));
    return request<{ data: WalletTransaction[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/wallet/transactions?${qs}`,
      { token, orgId },
    );
  },

  exportWalletTransactions: (
    token: string,
    orgId: string,
    params?: { start?: string; end?: string; type?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.type) qs.set('type', params.type);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/wallet/transactions/export?${qs}`,
      { token, orgId },
    );
  },

  // Reseller
  listResellerCustomers: (
    token: string,
    orgId: string,
    params?: { cursor?: string; limit?: number; status?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.status) qs.set('status', params.status);
    const query = qs.toString();
    return request<{ data: ResellerCustomer[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/reseller/customers${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  exportResellerCustomers: (
    token: string,
    orgId: string,
    params?: { start?: string; end?: string; status?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.status) qs.set('status', params.status);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/reseller/customers/export?${qs}`,
      { token, orgId },
    );
  },

  createResellerCustomer: (
    token: string,
    orgId: string,
    body: { businessName: string; legalName: string; contactEmail: string },
  ) =>
    request<{ data: { id: string; displayName: string; slug: string } }>(
      `/organizations/${orgId}/reseller/customers`,
      { method: 'POST', token, orgId, body: JSON.stringify(body) },
    ),

  allocateResellerCredit: (
    token: string,
    orgId: string,
    childOrgId: string,
    amount: number,
  ) =>
    request(`/organizations/${orgId}/reseller/customers/${childOrgId}/allocate`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify({ amount }),
    }),

  getResellerRevenue: (token: string, orgId: string) =>
    request<{ data: ResellerRevenue }>(`/organizations/${orgId}/reseller/revenue`, {
      token,
      orgId,
    }),

  // Admin — providers & reconciliation
  adminProviders: (token: string) =>
    request<{ data: AdminProvider[] }>('/admin/providers', { token }),

  adminExportProviderCallbacks: (
    token: string,
    params?: { start?: string; end?: string; providerAccountId?: string; orgId?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.providerAccountId) qs.set('providerAccountId', params.providerAccountId);
    if (params?.orgId) qs.set('orgId', params.orgId);
    return request<{ data: { csv: string; count: number } }>(
      `/admin/provider-callbacks/export?${qs}`,
      { token },
    );
  },

  adminUpdateProviderStatus: (token: string, providerId: string, status: string) =>
    request(`/admin/providers/${providerId}/status`, {
      method: 'POST',
      token,
      body: JSON.stringify({ status }),
    }),

  adminSetProviderCredentials: (
    token: string,
    accountId: string,
    credentials: Record<string, string>,
  ) =>
    request(`/admin/provider-accounts/${accountId}/credentials`, {
      method: 'POST',
      token,
      body: JSON.stringify({ credentials }),
    }),

  adminRoutingPreview: (token: string, params?: { countryCode?: string; segmentCount?: number }) => {
    const qs = new URLSearchParams();
    if (params?.countryCode) qs.set('countryCode', params.countryCode);
    if (params?.segmentCount != null) qs.set('segmentCount', String(params.segmentCount));
    return request<{ data: RoutingPreview }>(`/admin/routing/preview?${qs}`, { token });
  },

  adminUpdateRoute: (
    token: string,
    routeId: string,
    body: {
      priority?: number;
      weight?: number;
      status?: string;
      metadata?: { experimentId?: string; trafficPercent?: number };
    },
  ) =>
    request(`/admin/routes/${routeId}`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),

  adminListContracts: (
    token: string,
    params?: { cursor?: string; limit?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{ data: EnterpriseContract[]; pagination: CursorPagination }>(
      `/admin/contracts${query ? `?${query}` : ''}`,
      { token },
    );
  },

  adminExportContracts: (
    token: string,
    params?: { start?: string; end?: string; status?: string; orgId?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.status) qs.set('status', params.status);
    if (params?.orgId) qs.set('orgId', params.orgId);
    return request<{ data: { csv: string; count: number } }>(
      `/admin/contracts/export?${qs}`,
      { token },
    );
  },

  adminCreateContract: (
    token: string,
    orgId: string,
    body: {
      name: string;
      unitPriceMinor: number;
      countryCode?: string;
      currency?: string;
      validFrom: string;
      validTo?: string;
      contractReference?: string;
      tier?: 'enterprise' | 'volume' | 'custom';
    },
  ) =>
    request(`/admin/organizations/${orgId}/contracts`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    }),

  adminDeactivateContract: (token: string, contractId: string) =>
    request(`/admin/contracts/${contractId}/deactivate`, { method: 'POST', token }),

  adminEnablePostpaid: (token: string, orgId: string, creditLimitMinor: number) =>
    request(`/admin/organizations/${orgId}/billing/postpaid`, {
      method: 'POST',
      token,
      body: JSON.stringify({ creditLimitMinor }),
    }),

  adminDisablePostpaid: (token: string, orgId: string) =>
    request(`/admin/organizations/${orgId}/billing/prepaid`, { method: 'POST', token }),

  adminReconciliation: (token: string, params?: { start?: string; end?: string; orgId?: string }) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.orgId) qs.set('orgId', params.orgId);
    return request<{ data: ReconciliationReport }>(`/admin/reconciliation?${qs}`, { token });
  },

  adminExportReconciliation: (
    token: string,
    params?: { start?: string; end?: string; orgId?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.orgId) qs.set('orgId', params.orgId);
    return request<{ data: { csv: string; count: number } }>(
      `/admin/reconciliation/export?${qs}`,
      { token },
    );
  },

  adminMarginReport: (token: string, params?: { start?: string; end?: string; orgId?: string }) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.orgId) qs.set('orgId', params.orgId);
    return request<{ data: MarginReport }>(`/admin/margin-report?${qs}`, { token });
  },

  adminExportMarginReport: (
    token: string,
    params?: { start?: string; end?: string; orgId?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.orgId) qs.set('orgId', params.orgId);
    return request<{ data: { csv: string; count: number } }>(
      `/admin/margin-report/export?${qs}`,
      { token },
    );
  },

  adminIssueInvoice: (token: string, invoiceId: string) =>
    request(`/admin/invoices/${invoiceId}/issue`, { method: 'POST', token }),

  adminEnableReseller: (token: string, orgId: string) =>
    request(`/admin/organizations/${orgId}/reseller/enable`, { method: 'POST', token }),

  // Support
  listSupportTickets: (
    token: string,
    orgId: string,
    params?: { cursor?: string; limit?: number; status?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.status) qs.set('status', params.status);
    const query = qs.toString();
    return request<{ data: SupportTicket[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/support/tickets${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  exportSupportTickets: (
    token: string,
    orgId: string,
    params?: { status?: string; start?: string; end?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    return request<{ data: { csv: string; count: number } }>(
      `/organizations/${orgId}/support/tickets/export?${qs}`,
      { token, orgId },
    );
  },

  createSupportTicket: (
    token: string,
    orgId: string,
    body: { subject: string; body: string; priority?: string },
  ) =>
    request<{ data: SupportTicket }>(`/organizations/${orgId}/support/tickets`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  getSupportTicket: (token: string, orgId: string, ticketId: string) =>
    request<{ data: SupportTicketDetail }>(
      `/organizations/${orgId}/support/tickets/${ticketId}`,
      { token, orgId },
    ),

  replySupportTicket: (
    token: string,
    orgId: string,
    ticketId: string,
    body: string,
  ) =>
    request(`/organizations/${orgId}/support/tickets/${ticketId}/reply`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify({ body }),
    }),

  adminSupportTickets: (
    token: string,
    params?: { status?: string; orgId?: string; cursor?: string; limit?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.orgId) qs.set('orgId', params.orgId);
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{ data: SupportTicket[]; pagination: CursorPagination }>(
      `/admin/support/tickets${query ? `?${query}` : ''}`,
      { token },
    );
  },

  adminExportSupportTickets: (
    token: string,
    params?: { status?: string; orgId?: string; start?: string; end?: string },
  ) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.orgId) qs.set('orgId', params.orgId);
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    return request<{ data: { csv: string; count: number } }>(
      `/admin/support/tickets/export?${qs}`,
      { token },
    );
  },

  adminUpdateTicketStatus: (token: string, ticketId: string, status: string) =>
    request(`/admin/support/tickets/${ticketId}/status`, {
      method: 'POST',
      token,
      body: JSON.stringify({ status }),
    }),

  // Channels
  listUssdServices: (
    token: string,
    orgId: string,
    params?: { cursor?: string; limit?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{ data: UssdService[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/channels/ussd/services${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  createUssdService: (
    token: string,
    orgId: string,
    body: { name: string; shortCode: string; callbackUrl?: string },
  ) =>
    request(`/organizations/${orgId}/channels/ussd/services`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body),
    }),

  listVoiceNumbers: (
    token: string,
    orgId: string,
    params?: { cursor?: string; limit?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{ data: VoiceNumber[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/channels/voice/numbers${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  listWhatsappAccounts: (
    token: string,
    orgId: string,
    params?: { cursor?: string; limit?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{ data: WhatsappAccount[]; pagination: CursorPagination }>(
      `/organizations/${orgId}/channels/whatsapp/accounts${query ? `?${query}` : ''}`,
      { token, orgId },
    );
  },

  getResellerBranding: (token: string, orgId: string) =>
    request<{ data: ResellerBranding }>(`/organizations/${orgId}/reseller/branding`, {
      token,
      orgId,
    }),

  updateResellerBranding: (
    token: string,
    orgId: string,
    body: ResellerBranding,
  ) =>
    request(`/organizations/${orgId}/reseller/branding`, {
      method: 'POST',
      token,
      orgId,
      body: JSON.stringify(body),
    }),
};

export interface User {
  id: string;
  name: string;
  email: string;
  status: string;
  emailVerified: boolean;
  isStaff: boolean;
  staffRole: string | null;
}

export interface Organization {
  id: string;
  displayName: string;
  name?: string;
  legalName: string;
  slug: string;
  countryCode: string;
  currency: string;
  status: string;
  role?: string;
}

export interface OnboardingStatus {
  steps: Array<{
    key: string;
    label: string;
    completed: boolean;
    status?: string;
    balance?: number;
  }>;
  progress: number;
}

export interface DashboardKpi {
  value: number;
  deltaPct: number;
}

export interface DashboardStats {
  wallet: {
    currency: string;
    total: number;
    available: number;
    reserved: number;
  };
  messages: {
    sent: number;
    delivered: number;
    failed: number;
    deliveryRate: number;
  };
  monthlySpend: { amount: number; currency: string };
  recentCampaigns: DashboardCampaign[];
  recentMessages: DashboardMessage[];
  usageChart: Array<{
    date: string;
    sent: number;
    delivered: number;
    failed: number;
    spend: number;
  }>;
  kpis: {
    walletAvailable: DashboardKpi;
    messagesSent30d: DashboardKpi;
    deliveryRate: DashboardKpi;
    monthlySpend: DashboardKpi;
    todayMessages: DashboardKpi;
    activeCampaigns: DashboardKpi;
    failedMessages30d: DashboardKpi;
    contacts: DashboardKpi;
  };
  channelUsage: Array<{ channel: string; count: number }>;
  providerHealth: Array<{
    name: string;
    status: string;
    latencyMs: number | null;
    note: string;
  }>;
  deliveryScore: {
    score: number;
    label: string;
    deltaPct: number;
  };
  liveFeed: Array<{
    id: string;
    at: string;
    destination: string;
    sender: string;
    status: string;
    channel: string;
  }>;
  systemHealth: Array<{
    label: string;
    value: string;
    tone: 'success' | 'warning' | 'danger' | 'neutral';
  }>;
  operationalStatus: 'healthy' | 'degraded' | 'critical';
  recentActivity: Array<{ at: string; title: string }>;
}

export interface DashboardCampaign {
  id: string;
  name: string;
  status: string;
  audienceCount: number | null;
  createdAt: string;
}

export interface DashboardMessage {
  id: string;
  destination: string;
  sender: string;
  status: string;
  channel?: string;
  createdAt: string;
}

export interface DashboardSearchResults {
  messages: Array<{
    id: string;
    destination: string;
    sender: string;
    status: string;
    channel: string;
  }>;
  campaigns: Array<{ id: string; name: string; status: string }>;
  contacts: Array<{
    id: string;
    firstName: string | null;
    lastName: string | null;
    phoneE164: string;
  }>;
  senderIds: Array<{ id: string; value: string; status: string }>;
}

export interface AdminDashboard {
  stats: {
    organizations: number;
    users: number;
    messages: number;
    pendingKyc: number;
    pendingSenderIds: number;
    openTickets: number;
    messages30d: number;
    deliveryRate: number;
    failed30d: number;
    newUsers30d: number;
    activeUsers30d: number;
  };
  trafficChart: Array<{
    date: string;
    sent: number;
    delivered: number;
    failed: number;
    spend: number;
  }>;
  userSignupsChart: Array<{
    date: string;
    signups: number;
    active: number;
  }>;
  recentUsers: AdminUser[];
  providerHealth: Array<{
    name: string;
    status: string;
    note: string;
  }>;
  priorityQueue: Array<{
    id: string;
    organization: string;
    issue: string;
    status: string;
    owner: string;
    href: string;
  }>;
  recentOrganizations: AdminOrg[];
}

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  status: string;
  emailVerified: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  organizations: Array<{
    id: string;
    displayName: string;
    role: string;
  }>;
}

export interface AdminOrg {
  id: string;
  displayName: string;
  legalName?: string;
  slug?: string;
  status: string;
  countryCode: string;
  kycStatus?: string;
  memberCount?: number;
  messageCount?: number;
  createdAt: string;
}

export interface AdminKycProfile {
  id: string;
  status: string;
  businessType?: string | null;
  registeredAddress?: string | null;
  contactPerson?: string | null;
  submittedAt?: string | null;
  organization: {
    id: string;
    displayName: string;
    legalName: string;
    countryCode: string;
  };
}

export interface PostpaidCycleResult {
  periodStart: string;
  periodEnd: string;
  summary: {
    total: number;
    issued: number;
    skipped: number;
    errors: number;
  };
  results: Array<{
    organizationId: string;
    organizationName: string;
    status: 'issued' | 'skipped' | 'error';
    invoiceId?: string;
    reason?: string;
  }>;
}

export interface AdminPendingPayment {
  id: string;
  organizationId: string;
  organizationName: string;
  provider: string;
  externalReference: string;
  amountMinor: number;
  currency: string;
  status: string;
  createdAt: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface CursorPagination {
  has_more: boolean;
  next_cursor: string | null;
}

export interface Contact {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  phone: string;
  email?: string | null;
  consentStatus: string;
  tags: string[];
  createdAt: string;
}

export interface ImportResult {
  total: number;
  imported: number;
  duplicates: number;
  failed: number;
  errors: string[];
}

export interface ContactImport {
  id: string;
  status: string;
  filePath: string;
  totalRows: number;
  importedRows: number;
  duplicateRows: number;
  failedRows: number;
  createdAt: string;
}

export interface ContactGroup {
  id: string;
  name: string;
  description?: string | null;
  contactCount: number;
}

export interface ContactTag {
  id: string;
  name: string;
  contactCount: number;
}

export interface AdminPendingSenderId {
  id: string;
  value: string;
  countryCode: string;
  status: string;
  purpose?: string | null;
  organization?: { id: string; displayName: string; legalName?: string };
}

export interface SenderId {
  id: string;
  value: string;
  countryCode: string;
  status: string;
  purpose?: string | null;
  requestedAt: string;
  approvedAt?: string | null;
  rejectionReason?: string | null;
}

export interface SmsEstimate {
  to: string;
  encoding: string;
  segments: number;
  characterCount: number;
  estimatedCost: { amount: number; currency: string };
  wallet: { available: number; currency: string };
}

export interface SmsMessage {
  id: string;
  to: string;
  from: string;
  message: string;
  encoding: string;
  segments: number;
  status: string;
  estimatedCost: { amount: number; currency: string };
  createdAt: string;
  deliveredAt?: string | null;
  failureReason?: string | null;
}

export interface SmsMessageDetail extends SmsMessage {
  clientReference?: string | null;
  submittedAt?: string | null;
  failedAt?: string | null;
  events: Array<{
    status: string;
    providerStatus: string;
    occurredAt: string;
  }>;
}

export interface AuditLogEntry {
  id: string;
  action: string;
  resourceType: string;
  resourceId?: string | null;
  actor?: { id: string; name: string; email: string } | null;
  organization?: { id: string; name: string } | null;
  ipAddress?: string | null;
  createdAt: string;
}

export interface SmsTemplate {
  id: string;
  name: string;
  content: string;
  createdAt: string;
}

export interface SuppressionEntry {
  id: string;
  destination: string;
  reason?: string | null;
  source?: string | null;
  createdAt: string;
}

export interface WalletBalance {
  currency: string;
  total: number;
  available: number;
  reserved: number;
  billingMode?: 'prepaid' | 'postpaid';
  creditLimitMinor?: number;
  creditUsedMinor?: number;
  outstandingMinor?: number;
}

export interface Campaign {
  id: string;
  name: string;
  senderId: string;
  content: string;
  encoding: string;
  status: string;
  audienceCount: number;
  estimatedCost: { amount: number; currency: string };
  finalCost?: { amount: number; currency: string } | null;
  scheduledAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
}

export interface CampaignDetail extends Campaign {
  messageCount?: number;
}

export interface CampaignPreview {
  recipients: number;
  segments: number;
  encoding: string;
  estimatedCost: { amount: number; currency: string };
  walletAvailable: number;
}

export interface Payment {
  id: string;
  externalReference: string;
  amountMinor: number;
  currency: string;
  status: string;
  provider: string;
  createdAt: string;
  paidAt?: string | null;
}

export interface DevProject {
  id: string;
  name: string;
  environment: string;
  status: string;
  apiKeyCount: number;
}

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  lastUsedAt?: string | null;
  createdAt: string;
  project?: { name: string; environment: string };
}

export interface WebhookEndpoint {
  id: string;
  url: string;
  subscribedEvents: string[];
  status: string;
  failureCount: number;
  createdAt: string;
}

export interface WebhookDelivery {
  id: string;
  eventId: string;
  eventType: string;
  status: string;
  attempt: number;
  responseCode?: number | null;
  responseTimeMs?: number | null;
  deliveredAt?: string | null;
  createdAt: string;
  endpoint: { id: string; url: string };
}

export interface OrganizationMember {
  id: string;
  userId: string;
  name: string;
  email: string;
  role: { key: string; name: string };
  status: string;
  joinedAt: string;
}

export interface ApiLog {
  id: string;
  method: string;
  path: string;
  statusCode: number;
  latencyMs: number;
  createdAt: string;
}

export interface MessageAnalytics {
  period: { start: string; end: string };
  summary: {
    sent: number;
    delivered: number;
    failed: number;
    deliveryRate: number;
    spend: number;
  };
  byDay: Array<{ date: string; sent: number; delivered: number; spend: number }>;
  bySender: Array<{ sender: string; count: number }>;
}

export interface CampaignAnalytics {
  id: string;
  name: string;
  status: string;
  audienceCount: number;
  estimatedCostMinor: number;
  finalCostMinor: number | null;
  currency: string;
  completedAt: string | null;
  sent: number;
  delivered: number;
  failed: number;
  deliveryRate: number;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  currency: string;
  subtotal: { amount: number; currency: string };
  tax: { amount: number; currency: string };
  total: { amount: number; currency: string };
  amountPaid: { amount: number; currency: string };
  amountDue: { amount: number; currency: string };
  status: string;
  periodStart: string;
  periodEnd: string;
  issuedAt?: string | null;
  dueAt?: string | null;
  createdAt: string;
}

export interface InvoiceDetail extends Invoice {
  organization?: { displayName: string; legalName: string; taxNumber: string | null };
  items?: Array<{
    id: string;
    type: string;
    description: string;
    quantity: number;
    unitPrice: { amount: number; currency: string };
    amount: { amount: number; currency: string };
  }>;
}

export interface WalletTransaction {
  id: string;
  type: string;
  direction: string;
  amountMinor: number;
  balanceAfterMinor: number;
  description: string | null;
  createdAt: string;
}

export interface ResellerCustomer {
  id: string;
  displayName: string;
  legalName: string;
  status: string;
  countryCode: string;
  messageCount: number;
  memberCount: number;
  wallet: { currency: string; available: number; total: number } | null;
  createdAt: string;
}

export interface ResellerRevenue {
  customerCount: number;
  totalAllocated: number;
  customerMessageSpend: number;
  customerMessageCount: number;
}

export interface AdminProvider {
  id: string;
  key: string;
  name: string;
  status: string;
  accounts: Array<{
    id: string;
    name: string;
    countryCode: string | null;
    environment: string;
    status: string;
    credentialsConfigured: boolean;
    messageCount: number;
    callbackCount: number;
    routes: Array<{
      id: string;
      channel: string;
      countryCode: string;
      priority: number;
      weight: number | null;
      status: string;
      metadata?: { experimentId?: string; trafficPercent?: number };
    }>;
  }>;
}

export interface RoutingPreview {
  countryCode: string;
  segmentCount: number;
  routes: Array<{
    routeId: string;
    providerAccountId: string;
    providerKey: string;
    score: number;
    reasons: string[];
    providerCostMinor: number | null;
    accountStatus: string;
    providerStatus: string;
    failureRate24h: number;
    experimentId?: string;
    trafficPercent?: number;
    weight: number | null;
  }>;
}

export interface EnterpriseContract {
  id: string;
  name: string;
  status: string;
  currency: string;
  validFrom: string;
  validTo: string | null;
  organization: { id: string; displayName: string; slug: string } | null;
  sms: {
    ruleId: string;
    countryCode: string;
    unitPriceMinor: number;
    tier: string;
    contractReference: string | null;
  } | null;
}

export interface ReconciliationReport {
  period: { start: string; end: string };
  summary: {
    messageCount: number;
    delivered: number;
    failed: number;
    totalRevenueMinor: number;
    totalProviderCostMinor: number;
    grossMarginMinor: number;
    issueCount: number;
  };
  issues: Array<{
    type: string;
    messageId: string;
    organizationId: string;
    status: string;
    detail: string;
  }>;
}

export interface MarginReport {
  period: { start: string; end: string };
  routes: Array<{
    routeId: string | null;
    country: string;
    revenue: number;
    cost: number;
    count: number;
    margin: number;
  }>;
}

export interface SupportTicket {
  id: string;
  number: string;
  subject: string;
  status: string;
  priority: string;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
  organization?: { id: string; displayName: string };
}

export interface SupportTicketDetail extends SupportTicket {
  creator?: { id: string; name: string; email: string };
  messages?: Array<{ id: string; authorUserId: string; body: string; createdAt: string }>;
}

export interface UssdService {
  id: string;
  name: string;
  shortCode: string;
  status: string;
  callbackUrl?: string | null;
}

export interface VoiceNumber {
  id: string;
  number: string;
  countryCode: string;
  type: string;
  status: string;
}

export type IvrFlowStatus = 'DRAFT' | 'PUBLISHED' | 'PAUSED' | 'ARCHIVED';

export interface IvrNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: Record<string, unknown>;
}

export interface IvrEdge {
  id: string;
  source: string;
  sourceHandle?: string | null;
  target: string;
  targetHandle?: string | null;
  label?: string;
}

export interface IvrFlow {
  id: string;
  name: string;
  description?: string | null;
  status: IvrFlowStatus;
  defaultLanguage: string;
  assignedVoiceNumber?: string | null;
  entryNodeId?: string | null;
  version?: number;
  publishedVersion?: number;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  lastPublishedAt?: string;
  calls: number;
  nodes: IvrNode[];
  edges: IvrEdge[];
}

export interface IvrMetrics {
  totalFlows: number;
  publishedFlows: number;
  totalCalls: number;
  completionRate: number;
}

export interface IvrIssue {
  nodeId?: string | null;
  code: string;
  message: string;
}

export interface IvrValidationResult {
  valid: boolean;
  errors: IvrIssue[];
  stats?: Record<string, unknown>;
}

export interface IvrExecution {
  id: string;
  flowId: string;
  status: string;
  phoneLastFour?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  duration?: number | null;
  terminationReason?: string | null;
}

export interface IvrExecutionDetail extends IvrExecution {
  flowName?: string | null;
  currentNodeId?: string | null;
  context?: {
    language?: string | null;
    variables?: Record<string, unknown>;
  };
  events: Array<{
    id: string;
    nodeId?: string | null;
    nodeType?: string | null;
    eventType?: string | null;
    status?: string | null;
    safeOutput?: Record<string, unknown> | null;
    createdAt?: string | null;
  }>;
}

export interface IvrSimulation {
  id: string;
  flowId: string;
  status: string;
  currentNodeId?: string | null;
  messages?: string[];
  collect?: { variableName?: string; numDigits?: number; finishKey?: string; timeout?: number } | null;
  terminationReason?: string | null;
  context?: Record<string, unknown>;
  createdAt?: string;
}

export interface VoiceCall {
  id: string;
  from: string;
  to: string;
  direction: string;
  status: string;
  durationSec?: number | null;
  createdAt: string;
}

export interface WhatsappAccount {
  id: string;
  phoneNumberId: string;
  displayName?: string | null;
  status: string;
  templateCount?: number;
  credentialsConfigured?: boolean;
  createdAt?: string;
}

export interface WhatsappTemplate {
  id: string;
  whatsappAccountId: string;
  name: string;
  language: string;
  category: string;
  status: string;
  createdAt: string;
}

export interface UssdSession {
  id: string;
  ussdServiceId: string;
  sessionId: string;
  phoneNumber: string;
  status: string;
  currentMenu?: string | null;
  startedAt: string;
  endedAt?: string | null;
}

export interface ResellerBranding {
  portalName?: string;
  primaryColor?: string;
  logoUrl?: string;
  supportEmail?: string;
}

export interface AnalyticsInsights {
  anomalies: Array<{
    type: string;
    severity: string;
    message: string;
    metric: { current: number; baseline: number; unit: string };
  }>;
  recommendations: string[];
  bestSendDays: string[];
  healthScore: number;
}

const ZERO_DECIMAL_CURRENCIES = new Set(['TZS', 'KES', 'UGX', 'RWF', 'BIF']);

export function formatCurrency(amountMinor: number, currency = 'TZS') {
  const divisor = ZERO_DECIMAL_CURRENCIES.has(currency) ? 1 : 100;
  const amount = amountMinor / divisor;
  return new Intl.NumberFormat('en-TZ', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: ZERO_DECIMAL_CURRENCIES.has(currency) ? 0 : 2,
  }).format(amount);
}
